// src/contexts/AuthContext.js - Versão corrigida
import React, { createContext, useContext, useEffect, useState } from 'react';
import { authHelpers, supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [person, setPerson] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    // Get initial session
    initializeAuth();

    // Set up auth listener
    let subscription = null;
    
    if (supabase && supabase.auth && supabase.auth.onAuthStateChange) {
      const authListener = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state change:', event, session?.user?.id);
          
          if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
            if (event === 'SIGNED_OUT') {
              handleSignOut();
            } else if (event === 'TOKEN_REFRESHED' && session) {
              // Apenas atualizar session, manter outros dados
              setSession(session);
              setUser(session.user);
            }
          }
        }
      );
      
      if (authListener && authListener.data && authListener.data.subscription) {
        subscription = authListener.data.subscription;
      }
    }

    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      console.log('Initializing auth...');
      
      if (!authHelpers || !authHelpers.getSession) {
        console.log('Auth helpers not available, skipping session check');
        return;
      }
      
      const sessionData = await authHelpers.getSession();
      
      if (sessionData) {
        console.log('Session found, setting auth state');
        setUser(sessionData.user);
        setSession(sessionData.session);
        setPerson(sessionData.person);
        setCompany(sessionData.company);
      } else {
        console.log('No active session found');
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  };

  const handleSignOut = () => {
    console.log('Clearing auth state');
    setUser(null);
    setSession(null);
    setPerson(null);
    setCompany(null);
  };

  const signUp = async (userData) => {
    try {
      setLoading(true);
      console.log('Starting registration process...');
      
      if (!authHelpers || !authHelpers.signUp) {
        throw new Error('Sistema de autenticação não disponível');
      }
      
      // Mostrar toast de loading
      const loadingToast = toast.loading('Criando sua conta...');
      
      const result = await authHelpers.signUp(userData);
      console.log('Registration result:', result);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Set auth state immediately if successful
      if (result.user) {
        setUser(result.user);
        setSession(result.session || null);
        
        if (result.person && result.company) {
          setPerson(result.person);
          setCompany(result.company);
          console.log('Registration completed successfully');
          toast.success('Conta criada com sucesso!');
        } else {
          console.warn('Registration incomplete - missing person or company data');
          setPerson(null);
          setCompany(null);
          toast.success('Usuário criado! Complete o cadastro para continuar.');
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error during sign up:', error);
      
      // Show specific error messages
      let errorMessage = 'Erro ao criar conta. Tente novamente.';
      
      if (error.message?.includes('User already registered') || 
          error.message?.includes('já está cadastrado')) {
        errorMessage = 'Este e-mail já está cadastrado. Tente fazer login.';
      } else if (error.message?.includes('Password') || 
                 error.message?.includes('senha')) {
        errorMessage = 'Senha deve ter pelo menos 6 caracteres.';
      } else if (error.message?.includes('Invalid email') || 
                 error.message?.includes('email inválido')) {
        errorMessage = 'E-mail inválido. Verifique o formato.';
      } else if (error.message?.includes('tentativas')) {
        errorMessage = error.message;
      } else if (error.message?.includes('empresa') || 
                 error.message?.includes('company')) {
        errorMessage = 'Erro ao criar empresa. Verifique os dados e tente novamente.';
      }
      
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      console.log('Starting login process...');
      
      if (!authHelpers || !authHelpers.signIn) {
        throw new Error('Sistema de autenticação não disponível');
      }
      
      // Mostrar toast de loading
      const loadingToast = toast.loading('Fazendo login...');
      
      const result = await authHelpers.signIn(email, password);
      console.log('Login result:', result);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Set auth state immediately
      if (result.user) {
        setUser(result.user);
        setSession(result.session || null);
        
        if (result.person && result.company) {
          setPerson(result.person);
          setCompany(result.company);
          console.log('Login completed successfully');
          toast.success(`Bem-vindo, ${result.person.name}!`);
        } else {
          console.warn('Login incomplete - missing person or company data');
          setPerson(null);
          setCompany(null);
          toast.warning('Login realizado, mas dados do perfil não foram encontrados.');
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error during sign in:', error);
      
      // Show specific error messages
      let errorMessage = 'Erro ao fazer login. Tente novamente.';
      
      if (error.message?.includes('Invalid login credentials') || 
          error.message?.includes('incorretos')) {
        errorMessage = 'E-mail ou senha incorretos.';
      } else if (error.message?.includes('Email not confirmed') || 
                 error.message?.includes('não confirmado')) {
        errorMessage = 'E-mail não confirmado. Verifique sua caixa de entrada.';
      } else if (error.message?.includes('perfil') || 
                 error.message?.includes('profile')) {
        errorMessage = 'Perfil não encontrado. Entre em contato com o suporte.';
      }
      
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      console.log('Starting signout process...');
      
      if (authHelpers && authHelpers.signOut) {
        await authHelpers.signOut();
      }
      
      handleSignOut();
      toast.success('Logout realizado com sucesso');
    } catch (error) {
      console.error('Error during sign out:', error);
      // Mesmo que falhe, limpar estado local
      handleSignOut();
      toast.error('Erro ao fazer logout, mas você foi desconectado localmente');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      if (!authHelpers || !authHelpers.resetPassword) {
        throw new Error('Sistema de recuperação não disponível');
      }
      
      const loadingToast = toast.loading('Enviando e-mail de recuperação...');
      
      await authHelpers.resetPassword(email);
      
      toast.dismiss(loadingToast);
      toast.success('E-mail de recuperação enviado!');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Erro ao enviar e-mail de recuperação');
      throw new Error('Erro ao enviar e-mail de recuperação');
    }
  };

  // Helper functions
  const isAuthenticated = !!user && !!person && !!company;
  const isAdmin = person?.type === 'admin';
  const isCollaborator = person?.type === 'collaborator';
  const hasPermission = (permission) => {
    if (isAdmin) return true;
    // Add more permission logic here
    return false;
  };

  // Debug info
  const getDebugInfo = () => {
    return {
      user: !!user,
      session: !!session,
      person: !!person,
      company: !!company,
      loading,
      initializing,
      isAuthenticated,
      personType: person?.type,
      companyName: company?.name
    };
  };

  const value = {
    // State
    user,
    session,
    person,
    company,
    loading,
    initializing,
    
    // Auth methods
    signUp,
    signIn,
    signOut,
    resetPassword,
    
    // Helper methods
    isAuthenticated,
    isAdmin,
    isCollaborator,
    hasPermission,
    
    // Debug
    getDebugInfo,
    
    // Refresh methods
    refreshSession: initializeAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;